import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HelpCircle, Tag, Clock, Lightbulb, AlertTriangle, Users, Monitor, ChevronDown, ChevronUp } from 'lucide-react';
import StandardTextAreaField from '@/components/ui/form-fields/StandardTextAreaField';
import { StandardTextField } from '@/components/ui/form-fields/StandardTextField';
import { StandardSelect } from '@/components/ui/form-fields/StandardSelect';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { RequestAppFormData } from './RequestAppModal';

interface RequestAppFormProps {
  formData: RequestAppFormData;
  onChange: (field: keyof RequestAppFormData, value: string) => void;
}

const RequestAppForm: React.FC<RequestAppFormProps> = ({ formData, onChange }) => {
  const [showProgressiveFields, setShowProgressiveFields] = useState(false);
  const [showOptionalDetails, setShowOptionalDetails] = useState(false);
  const [detectedCategory, setDetectedCategory] = useState<string>('');

  // Progressive disclosure logic
  useEffect(() => {
    const shouldShowProgressive = formData.problem.length >= 50;
    setShowProgressiveFields(shouldShowProgressive);

    // Auto-detect category based on keywords
    if (formData.problem.length >= 20) {
      const text = formData.problem.toLowerCase();
      let category = '';
      
      // Productivity keywords - expanded
      const productivityKeywords = [
        'task', 'todo', 'organize', 'schedule', 'workflow', 'automation', 'efficiency', 'project', 
        'deadline', 'calendar', 'reminder', 'planner', 'productivity', 'management', 'tracking',
        'focus', 'goal', 'objective', 'milestone', 'progress', 'kanban', 'scrum', 'agile',
        'collaboration', 'meeting', 'note', 'document', 'sync', 'backup',
        'dashboard', 'report', 'analytics', 'metrics', 'kpi', 'performance', 'optimization',
        'checklist', 'routine', 'habit', 'daily', 'weekly', 'monthly', 'recurring', 'repeat',
        'priority', 'urgent', 'important', 'due', 'overdue'
      ];

      // Creative keywords - expanded  
      const creativeKeywords = [
        'design', 'art', 'photo', 'image', 'graphic', 'visual', 'creative', 'drawing',
        'sketch', 'paint', 'illustration', 'logo', 'brand', 'color', 'palette', 'typography',
        'font', 'layout', 'composition', 'aesthetic', 'style', 'theme', 'mood', 'inspiration',
        'portfolio', 'gallery', 'showcase', 'exhibition', 'artwork', 'masterpiece', 'canvas',
        'digital art', 'vector', 'raster', 'pixel', 'resolution', 'editing', 'filter', 'effect',
        'animation', 'motion', 'film', 'movie', 'cinema', 'montage',
        'music', 'audio', 'sound', 'recording', 'mixing', 'mastering', 'composition', 'melody',
        'writing', 'story', 'novel', 'blog', 'copywriting', 'journalism', 'poetry'
      ];

      // Education keywords - expanded
      const educationKeywords = [
        'learn', 'study', 'course', 'education', 'training', 'teaching', 'tutorial', 'lesson',
        'class', 'school', 'university', 'college', 'academy', 'institute', 'curriculum',
        'syllabus', 'assignment', 'homework', 'exam', 'test', 'quiz', 'grade', 'score',
        'knowledge', 'skill', 'competency', 'certification', 'diploma', 'degree', 'qualification',
        'research', 'thesis', 'dissertation', 'paper', 'publication', 'journal', 'academic',
        'student', 'teacher', 'instructor', 'professor', 'mentor', 'tutor', 'coach',
        'language', 'vocabulary', 'grammar', 'pronunciation', 'fluency', 'translation',
        'math', 'science', 'history', 'literature', 'physics', 'chemistry', 'biology',
        'programming', 'coding', 'development', 'algorithm', 'data structure', 'computer science'
      ];

      // Finance keywords - expanded
      const financeKeywords = [
        'money', 'budget', 'finance', 'invest', 'investment', 'portfolio', 'stock', 'trading',
        'crypto', 'cryptocurrency', 'bitcoin', 'ethereum', 'blockchain', 'wallet', 'exchange',
        'bank', 'banking', 'account', 'savings', 'checking', 'loan', 'mortgage', 'credit',
        'debt', 'payment', 'transaction', 'transfer', 'wire', 'deposit', 'withdrawal',
        'expense', 'income', 'revenue', 'profit', 'loss', 'tax', 'taxes', 'deduction',
        'accounting', 'bookkeeping', 'ledger', 'balance', 'statement', 'invoice', 'receipt',
        'insurance', 'premium', 'claim', 'coverage', 'policy', 'retirement', 'pension',
        'spending', 'purchase', 'cost', 'price', 'value', 'worth', 'asset', 'liability',
        'cash', 'currency', 'dollar', 'euro', 'pound', 'yen', 'exchange rate', 'forex'
      ];

      // Health keywords - expanded
      const healthKeywords = [
        'health', 'fitness', 'exercise', 'workout', 'training', 'gym', 'sport', 'activity',
        'wellness', 'wellbeing', 'lifestyle', 'healthy', 'nutrition', 'diet', 'meal',
        'calorie', 'protein', 'carb', 'fat', 'vitamin', 'mineral', 'supplement', 'organic',
        'weight', 'bmi', 'body', 'muscle', 'strength', 'cardio', 'endurance', 'flexibility',
        'yoga', 'meditation', 'mindfulness', 'stress', 'anxiety', 'depression', 'mental health',
        'therapy', 'counseling', 'psychology', 'psychiatry', 'mood', 'emotion', 'feeling',
        'medical', 'doctor', 'physician', 'nurse', 'hospital', 'clinic', 'appointment',
        'symptom', 'diagnosis', 'treatment', 'medication', 'prescription', 'pharmacy',
        'sleep', 'rest', 'recovery', 'fatigue', 'energy', 'vitality', 'immune', 'disease'
      ];

      // Entertainment keywords - expanded
      const entertainmentKeywords = [
        'game', 'gaming', 'play', 'fun', 'entertainment', 'leisure', 'hobby', 'recreation',
        'video game', 'console', 'pc gaming', 'mobile game', 'arcade', 'puzzle', 'strategy',
        'movie', 'film', 'cinema', 'tv', 'television', 'series', 'show', 'episode', 'season',
        'streaming', 'netflix', 'youtube', 'media', 'broadcast',
        'song', 'album', 'artist', 'band', 'concert', 'festival', 'playlist',
        'chat', 'message', 'friend', 'community', 'network', 'platform',
        'book', 'reading', 'novel', 'magazine', 'comic', 'manga', 'literature',
        'travel', 'vacation', 'trip', 'adventure', 'explore', 'tourism', 'destination',
        'party', 'event', 'celebration', 'gathering', 'meetup'
      ];

      // Shopping keywords - expanded
      const shoppingKeywords = [
        'shop', 'shopping', 'buy', 'purchase', 'store', 'retail', 'marketplace', 'ecommerce',
        'cart', 'checkout', 'payment', 'order', 'delivery', 'shipping', 'tracking',
        'product', 'item', 'goods', 'merchandise', 'inventory', 'stock', 'catalog',
        'price', 'cost', 'deal', 'discount', 'sale', 'coupon', 'promo', 'offer', 'bargain',
        'review', 'rating', 'feedback', 'recommendation', 'comparison', 'wishlist',
        'brand', 'vendor', 'supplier', 'manufacturer', 'quality', 'warranty', 'return',
        'fashion', 'clothing', 'apparel', 'shoes', 'accessories', 'jewelry', 'beauty',
        'electronics', 'gadget', 'tech', 'computer', 'phone', 'tablet', 'appliance',
        'grocery', 'beverage', 'restaurant', 'dining', 'takeout'
      ];

      // Check each category
      if (productivityKeywords.some(keyword => text.includes(keyword))) {
        category = 'productivity';
      } else if (creativeKeywords.some(keyword => text.includes(keyword))) {
        category = 'creative';
      } else if (educationKeywords.some(keyword => text.includes(keyword))) {
        category = 'education';
      } else if (financeKeywords.some(keyword => text.includes(keyword))) {
        category = 'finance';
      } else if (healthKeywords.some(keyword => text.includes(keyword))) {
        category = 'health';
      } else if (entertainmentKeywords.some(keyword => text.includes(keyword))) {
        category = 'entertainment';
      } else if (shoppingKeywords.some(keyword => text.includes(keyword))) {
        category = 'shopping';
      }

      if (category && category !== detectedCategory) {
        setDetectedCategory(category);
        if (!formData.category) {
          onChange('category', category);
        }
      }
    }
  }, [formData.problem, formData.category, onChange, detectedCategory]);

  const categoryOptions = [
    { value: 'productivity', label: 'Productivity' },
    { value: 'creative', label: 'Creative' },
    { value: 'education', label: 'Education' },
    { value: 'finance', label: 'Finance' },
    { value: 'health', label: 'Health' },
    { value: 'entertainment', label: 'Entertainment' },
    { value: 'shopping', label: 'Shopping' }
  ];

  const frequencyOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'occasionally', label: 'Occasionally' }
  ];

  const priorityOptions = [
    { value: 'critical', label: 'Critical - I need this urgently' },
    { value: 'important', label: 'Important - Would help a lot' },
    { value: 'nice-to-have', label: 'Nice to have - When you have time' }
  ];

  const sharingOptions = [
    { value: 'solo', label: 'Just for me' },
    { value: 'team', label: 'For my team' },
    { value: 'both', label: 'Both personal and team use' }
  ];

  const platformOptions = [
    { value: 'desktop', label: 'Desktop only' },
    { value: 'mobile', label: 'Mobile only' },
    { value: 'both', label: 'Both desktop and mobile' }
  ];

  const characterCount = formData.problem.length;

  // Helper function to handle select changes
  const handleSelectChange = (field: keyof RequestAppFormData) => (value: string | string[]) => {
    onChange(field, Array.isArray(value) ? value[0] || '' : value);
  };

  return (
    <div className="space-y-6">
      {/* Step 1: Problem Description (Always Visible) */}
      <div className="space-y-2">
        <StandardTextAreaField
          id="problem"
          label="What problem are you trying to solve?"
          icon={<HelpCircle size={16} />}
          value={formData.problem}
          onChange={(value) => onChange('problem', value)}
          placeholder="e.g., I need to track my daily habits and see progress over time, or I want to manage my team's project deadlines more effectively..."
          rows={4}
          required={true}
          className="resize-none"
        />
        
        {/* Character count indicator */}
        <div className="flex justify-between items-center text-xs">
          <div className={cn(
            "transition-colors duration-200",
            characterCount < 50 ? "text-amber-600 dark:text-amber-400" :
            "text-green-600 dark:text-green-400"
          )}>
            {characterCount < 50 ? `${50 - characterCount} more characters needed` :
             "Perfect length!"}
          </div>
          <div className="text-muted-foreground font-mono">
            {characterCount}
          </div>
        </div>
      </div>

      {/* Step 2: Progressive Fields (Auto-shown when 50+ characters) */}
      <AnimatePresence>
        {showProgressiveFields && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="space-y-4 overflow-hidden"
          >
            <Separator className="my-4" />
            
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground/80 flex items-center">
                  <Tag size={16} className="mr-1" />
                  What category best fits this?
                </label>
                <StandardSelect
                  id="category"
                  label=""
                  icon={<Tag size={16} />}
                  value={formData.category || ''}
                  onChange={handleSelectChange('category')}
                  options={categoryOptions}
                  placeholder="Select a category"
                  className={detectedCategory && !formData.category ? "ring-2 ring-purple-400/50" : ""}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground/80 flex items-center">
                  <Clock size={16} className="mr-1" />
                  How often would you use this?
                </label>
                <StandardSelect
                  id="frequency"
                  label=""
                  icon={<Clock size={16} />}
                  value={formData.frequency || ''}
                  onChange={handleSelectChange('frequency')}
                  options={frequencyOptions}
                  placeholder="Select frequency"
                />
              </div>
            </div>

            <StandardTextField
              id="currentSolution"
              label="How do you handle this now?"
              icon={<Lightbulb size={16} />}
              value={formData.currentSolution}
              onChange={(value) => onChange('currentSolution', value)}
              placeholder="e.g., I use spreadsheets but they're hard to maintain..."
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Step 3: Optional Details (Expandable) */}
      {showProgressiveFields && (
        <div className="space-y-4">
          <motion.button
            type="button"
            onClick={() => setShowOptionalDetails(!showOptionalDetails)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {showOptionalDetails ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            {showOptionalDetails ? 'Hide' : 'Show'} optional details
            <span className="text-xs bg-muted px-2 py-1 rounded-full">
              Helps us prioritize
            </span>
          </motion.button>

          <AnimatePresence>
            {showOptionalDetails && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="space-y-4 overflow-hidden"
              >
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground/80 flex items-center">
                      <AlertTriangle size={16} className="mr-1" />
                      How urgent is this for you?
                    </label>
                    <StandardSelect
                      id="priority"
                      label=""
                      icon={<AlertTriangle size={16} />}
                      value={formData.priority || ''}
                      onChange={handleSelectChange('priority')}
                      options={priorityOptions}
                      placeholder="Select priority level"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground/80 flex items-center">
                      <Users size={16} className="mr-1" />
                      Who would use this app?
                    </label>
                    <StandardSelect
                      id="sharing"
                      label=""
                      icon={<Users size={16} />}
                      value={formData.sharing || ''}
                      onChange={handleSelectChange('sharing')}
                      options={sharingOptions}
                      placeholder="Select usage type"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground/80 flex items-center">
                    <Monitor size={16} className="mr-1" />
                    Where would you primarily use this?
                  </label>
                  <StandardSelect
                    id="platform"
                    label=""
                    icon={<Monitor size={16} />}
                    value={formData.platform || ''}
                    onChange={handleSelectChange('platform')}
                    options={platformOptions}
                    placeholder="Select platform preference"
                  />
                </div>

                <StandardTextAreaField
                  id="additionalDetails"
                  label="Any additional details or specific features?"
                  value={formData.additionalDetails}
                  onChange={(value) => onChange('additionalDetails', value)}
                  placeholder="e.g., I'd love to have notifications, or it should integrate with my calendar..."
                  rows={3}
                  className="resize-none"
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Helpful hints */}
      {characterCount > 0 && characterCount < 50 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg"
        >
          <p className="text-sm text-amber-800 dark:text-amber-200">
            💡 <strong>Tip:</strong> The more details you provide, the better we can understand your needs and build something perfect for you!
          </p>
        </motion.div>
      )}

      {detectedCategory && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg"
        >
          <p className="text-sm text-purple-800 dark:text-purple-200">
            🎯 <strong>Smart Detection:</strong> This sounds like a {detectedCategory} app! We've pre-selected the category for you.
          </p>
        </motion.div>
      )}
    </div>
  );
};

export default RequestAppForm; 